/** @file Viewport: the puppet drawn by the game runtime on a 3D grid, with tools, gizmo, picking and HUD. */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { pushContext } from '@zilo/pose3d-shortcuts/pushContext';
import { registerBindings } from '@zilo/pose3d-shortcuts/registerBindings';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { artClient } from '../art/artClient';
import type { ArtDeps, FitMode, MisfitImage } from '../art/artTypes';
import { BulkFitPrompt } from '../art/BulkFitPrompt';
import { OrientationCube } from './OrientationCube';
import { viewportBindings } from './viewportBindings';
import type { ViewportMode } from './viewportCtx';
import type { ViewportHud } from './viewportHud';
import { viewportRuntime } from './viewportRuntime';
import { ViewportStatus } from './ViewportStatus';
import { VIEWPORT_STYLES as S } from './viewportStyles';
import { ViewportToolbar } from './ViewportToolbar';

/** The message over the view while there is no bundle to draw (or the last rebuild failed). */
const noteOf = (error: string | null, hasBundle: boolean, loading: boolean): string | null =>
  (error !== null ? `Preview failed: ${error}` : hasBundle ? null : loading ? 'Building preview...' : 'No preview yet');

/** A fit question waiting for Pad / Scale / Cancel: the misfits and the promise's resolve. */
type FitAsk = { misfits: MisfitImage[]; resolve: (fit: FitMode | null) => void };

/**
 * `edit`: toolbar, picking, gizmo, part quads with their handles, drops and pastes onto parts (`deps` uploads them:
 * the Art calls of `session.url` unless given; dev pages pass their own) with the Art fit prompt, context menus, the `viewport` key table (active while the pointer is over the
 * view) and the status line; `turntable` (Art): the puppet, grid and orientation cube, drag orbits. One Babylon engine per
 * mount, disposed on unmount with its render loop, listeners and key registration.
 */
export function Viewport({ mode, deps }: { mode: ViewportMode; deps?: ArtDeps }): ReactElement {
  const store = useEditorStore();
  const url = useEditor((s) => s.session.url);
  const server = useMemo(() => deps ?? artClient(url), [deps, url]);
  const [ask, setAsk] = useState<FitAsk | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [hud, setHud] = useState<ViewportHud | null>(null);
  const hasBundle = useEditor((s) => s.preview.bundle !== null);
  const loading = useEditor((s) => s.preview.loading);
  const error = useEditor((s) => s.preview.error);
  useEffect(() => {
    if (box.current === null || canvas.current === null) return undefined;
    const askFit = (misfits: MisfitImage[]): Promise<FitMode | null> => new Promise((resolve) => { setAsk({ misfits, resolve }); });
    const runtime = viewportRuntime({ container: box.current, canvas: canvas.current }, store, { mode, onHud: setHud, deps: server, askFit });
    const unbind = mode === 'edit' ? registerBindings('viewport', viewportBindings, runtime.handlers) : () => undefined;
    return () => { unbind(); runtime.dispose(); };
  }, [store, mode, server]);
  const pop = useRef<(() => void) | null>(null);
  const enter = (): void => { if (mode === 'edit' && pop.current === null) pop.current = pushContext('viewport'); };
  const leave = (): void => { pop.current?.(); pop.current = null; };
  useEffect(() => leave, []);
  const note = noteOf(error, hasBundle, loading);
  return (
    <div style={S.root} data-viewport={mode}>
      {mode === 'edit' && <ViewportToolbar scale={hud?.scale ?? 1} />}
      <div ref={box} style={S.box} onPointerEnter={enter} onPointerLeave={leave}>
        <canvas ref={canvas} data-testid="viewport-canvas" />
        <OrientationCube facingDeg={hud?.facingDeg ?? 0} />
        {note !== null && <div style={S.note}>{note}</div>}
      </div>
      {mode === 'edit' && <ViewportStatus hud={hud} />}
      {ask !== null && <BulkFitPrompt misfits={ask.misfits} choose={(fit) => { setAsk(null); ask.resolve(fit); }} />}
    </div>
  );
}
